import { inject, observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Col, Container, Label, Row } from 'reactstrap';

import useActiveProduct from '../../../../hooks/product/useActiveProduct';
import useMaxQuantity from '../../../../hooks/quantity/useMaxQuantity';
import { createForm } from '../../../../models/Form';
import { createFormField } from '../../../../models/FormField';
import { modelOf } from '../../../../prop-types';
import AccountStore from '../../../../store/AccountStore';
import CheckoutStore from '../../../../store/CheckoutStore';
import ConfigStore from '../../../../store/ConfigStore';
import ProductAvailabilityType from '../../../../types/ProductAvailabilityType';
import ProductClass from '../../../../types/ProductClass';
import {
  productCountValid,
  quantityValid,
} from '../../../../util/formValidators';
import CustomCol from '../../../bootstrap/CustomCol';
import PageTitle from '../../../common/PageTitle';
import WysiwygContent from '../../../common/WysiwygContent';
import PriceSecondary from '../../../product/PriceSecondary';
import PriceWrapper from '../../../product/PriceWrapper';
import ProductAvailabilityText from '../../../product/ProductAvailabilityText';
import ProductDescription from '../../../product/ProductDescription';
import ProductFeatureImages from '../../../product/ProductFeatureImages';
import ProductImages from '../../../product/ProductImages';
import ProductImageSigns from '../../../product/ProductImageSigns';
import ProductPagePrice from '../../../product/ProductPagePrice';
import ProductPickers from '../../../product/ProductPickers';
import CheckoutAddToCart from '../CheckoutAddToCart';

const CheckoutCartModalContent = ({
  accountStore,
  checkoutStore,
  configStore,
}) => {
  const withTax = accountStore.showPricesWithTax;
  const product = checkoutStore.checkoutCartModal.selectedProduct;
  let defaultId = product.id;

  const productForDiscountCalculations =
    product.isMulti && product.getQuantityDiscounts().discount?.getLevels()
      ? product.multi.getChildWithBestDiscount()
      : product;
  const quantityDiscountDisplayStyle =
    configStore.product.productQuantityDiscountDisplayStyle;
  const { hasPriceRange } = product.getDiscountInfo(
    withTax,
    quantityDiscountDisplayStyle
  );
  const price = <PriceWrapper product={product} />;
  const quantityStep = product.getQuantityStep();

  const { getActiveCollectionElementIds } = useActiveProduct({
    productPage: false,
    checkoutPage: true,
  });
  const [activeProductId, setActiveProductId] = useState(defaultId);
  const addToCartProduct = product.getActualProduct(activeProductId) || product;
  const activeCollectionElementIds = getActiveCollectionElementIds({
    product,
    activeProductId,
  });

  const [getMaxQuantity] = useMaxQuantity({
    accountStore,
    configStore,
    addToCartProduct,
  });

  const maxQuantity = getMaxQuantity();

  const form = useMemo(() => {
    const formFields = {};

    formFields.quantity = createFormField(
      {
        value: quantityStep,
      },
      [
        quantityValid(maxQuantity, quantityStep),
        productCountValid(quantityStep),
      ]
    );

    return createForm(formFields);
  }, [activeProductId]);

  const renderProductAvailabilityText = () => {
    if (product.isCollection) {
      const activeProduct =
        product.collection.getItemWithProductId(activeProductId)?.product ||
        product;
      return (
        <ProductAvailabilityText
          availabilityHtml={activeProduct.availability_html}
        />
      );
    }

    if (product.class === ProductClass.SINGLE) {
      return (
        <ProductAvailabilityText availabilityHtml={product.availability_html} />
      );
    }

    return null;
  };

  const renderShortDescription = () => {
    if (!configStore.product.showShortDescription) {
      return null;
    }

    return (
      <ProductDescription
        className="CheckoutCartModalContent__short-description"
        product={product}
        isProductPage
      />
    );
  };

  const ifShowModel = () => {
    if (product.class === ProductClass.MULTI) {
      return (
        configStore.productPage.showMultiProductModel &&
        configStore.product.showModel
      );
    }

    return configStore.product.showModel;
  };

  const renderModel = () => {
    const addToCartProduct =
      product.getActualProduct(activeProductId) || product;

    const showModel = ifShowModel();

    if (showModel && addToCartProduct.model) {
      return (
        <div className="CheckoutCartModalContent__model">
          {addToCartProduct.model}
        </div>
      );
    }

    return null;
  };

  const renderTitleBlock = () => {
    const productTitle = product.multiproduct_title || product.name;

    return (
      <div className="CheckoutCartModalContent__title">
        <PageTitle>{productTitle}</PageTitle>
        {renderModel()}
      </div>
    );
  };

  return (
    <Container className="CheckoutCartModalContent">
      <Row>
        <CustomCol
          xs="12"
          md="7"
          className="CheckoutCartModalContent__column-images"
        >
          <ProductImages
            key={product.id}
            product={product}
            size="large"
            lazyLoading={false}
            activeColumnId={activeCollectionElementIds.columnId}
            activeRowId={activeCollectionElementIds.rowId}
            activeProductId={activeProductId || null}
            mainImageOverlay={
              <div className="CheckoutCartModalContent__product-images-overlay">
                <ProductFeatureImages
                  product={product}
                  position={'product_image'}
                />
                <ProductImageSigns product={product} />
              </div>
            }
          />
        </CustomCol>
        <CustomCol
          xs="12"
          md="5"
          className="CheckoutCartModalContent__column-info"
        >
          <div className="CheckoutCartModalContent__top-element">
            {renderTitleBlock()}
            {renderProductAvailabilityText()}
            {configStore.product.shortDescriptionTop &&
              renderShortDescription()}
          </div>
          <ProductPagePrice
            price={price}
            secondaryPrice={
              (configStore.product.showSecondaryTaxPrice ||
                accountStore.isRetailer) && <PriceSecondary product={product} />
            }
            isMulti={product.isMulti}
            product={productForDiscountCalculations}
            quantityDiscounts={
              addToCartProduct.getQuantityDiscounts().discounts
            }
            withDiscountUpTo={hasPriceRange}
            withDiscountBalloon={!hasPriceRange}
            withDiscountSavings={!hasPriceRange}
            discountInverted={hasPriceRange}
          />
          <ProductPickers
            product={product}
            activeProductId={activeProductId}
            onProductChange={setActiveProductId}
            activeCollectionElementIds={activeCollectionElementIds}
            checkoutCart
          />
          <CheckoutAddToCart
            form={form}
            activeProductId={activeProductId}
            product={addToCartProduct}
            maxQuantity={maxQuantity}
            quantityStep={quantityStep}
          />
        </CustomCol>
      </Row>
      {addToCartProduct?.description_long && (
        <Row>
          <Col>
            <div className="CheckoutCartModalContent__product-description">
              <Label className="CheckoutCartModalContent__product-description-label">
                <FormattedMessage
                  id="product.descriptionTitle"
                  defaultMessage="Description"
                />
              </Label>
              <WysiwygContent
                html={addToCartProduct.description_long}
                className="CheckoutCartModalContent__product-description-content"
              />
            </div>
          </Col>
        </Row>
      )}
    </Container>
  );
};

CheckoutCartModalContent.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  checkoutStore: modelOf(CheckoutStore).isRequired,
  configStore: modelOf(ConfigStore).isRequired,
};

export default inject(
  'accountStore',
  'checkoutStore',
  'configStore'
)(observer(CheckoutCartModalContent));
